import type { BetaSignupRequest } from '@nolens/domain';
import { Banner, Button, Field, Input, Logo, Select, useLocale } from '@nolens/ui';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { api, ApiError } from '../lib/api';
import { betaText } from '../i18n/beta';
import { publicPaths } from '../i18n/landing';

const SIZES = ['1', '2-5', '6-20', '21-100', '100+'] as const;

/** Inscrição para testar (pública, sem conta). As inscrições aparecem no portal /admin. */
export function BetaPage() {
  const locale = useLocale();
  const t = betaText(locale);
  const [form, setForm] = useState<Required<Omit<BetaSignupRequest, 'locale'>>>({
    email: '',
    name: '',
    company: '',
    role: '',
    teamSize: '',
    area: '',
    tools: '',
  });
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));
  const send = useMutation({
    mutationFn: () =>
      api('/public/beta-signups', {
        method: 'POST',
        anonymous: true,
        body: { ...form, locale } satisfies BetaSignupRequest,
      }),
  });
  useEffect(() => {
    const before = document.title;
    document.title = t.docTitle;
    return () => {
      document.title = before;
    };
  }, [t.docTitle]);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    send.mutate();
  };
  return (
    <main className="mx-auto max-w-xl px-5 py-10" data-beta>
      <Link to={publicPaths(locale).product} aria-label="Nolens Guides">
        <Logo />
      </Link>
      <p className="mt-10 text-[14px] font-medium text-accent-text">{t.eyebrow}</p>
      <h1 className="mt-2 text-3xl font-semibold tracking-tight">{t.title}</h1>
      <p className="mt-3 text-text-2">{t.lead}</p>
      <p className="mt-3 text-[13.5px] text-text-3">{t.perks}</p>
      {send.isSuccess ? (
        <div className="mt-8" data-beta-done>
          <Banner tone="positive">
            <strong>{t.doneTitle}</strong> {t.doneText}
          </Banner>
          <p className="mt-6 text-text-2">{t.doneNext}</p>
          <Link
            to="/entrar"
            className="mt-4 inline-flex h-11 items-center rounded-full bg-accent px-5 font-semibold text-on-accent hover:brightness-95"
            data-beta-signin
          >
            {t.doneSignIn} →
          </Link>
          <p className="mt-6 text-sm">
            <Link to={publicPaths(locale).product} className="underline">
              {t.back}
            </Link>
          </p>
        </div>
      ) : (
        <form className="mt-8 grid gap-4" onSubmit={submit}>
          <Field label={t.email}>
            {(p) => (
              <Input
                {...p}
                type="email"
                required
                autoComplete="email"
                value={form.email}
                onChange={set('email')}
              />
            )}
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t.name}>
              {(p) => <Input {...p} autoComplete="name" value={form.name} onChange={set('name')} />}
            </Field>
            <Field label={t.company}>
              {(p) => (
                <Input
                  {...p}
                  autoComplete="organization"
                  value={form.company}
                  onChange={set('company')}
                />
              )}
            </Field>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t.role}>
              {(p) => (
                <Input
                  {...p}
                  placeholder={t.rolePlaceholder}
                  value={form.role}
                  onChange={set('role')}
                />
              )}
            </Field>
            <Field label={t.teamSize}>
              {(p) => (
                <Select {...p} value={form.teamSize} onChange={set('teamSize')}>
                  <option value="">{t.choose}</option>
                  {SIZES.map((s) => (
                    <option key={s} value={s}>
                      {s === '1' ? t.just : s}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>
          <Field label={t.area}>
            {(p) => (
              <Input
                {...p}
                placeholder={t.areaPlaceholder}
                value={form.area}
                onChange={set('area')}
                maxLength={60}
              />
            )}
          </Field>
          <Field label={t.tools}>
            {(p) => (
              <Input
                {...p}
                placeholder={t.toolsPlaceholder}
                value={form.tools}
                onChange={set('tools')}
                maxLength={500}
              />
            )}
          </Field>
          {send.error instanceof ApiError && <Banner tone="critical">{send.error.message}</Banner>}
          <Button type="submit" size="lg" loading={send.isPending}>
            {t.submit}
          </Button>
          <p className="text-[12.5px] text-text-3">
            {t.privacy}{' '}
            <Link to={publicPaths(locale).privacy} className="underline">
              {t.privacyLink}
            </Link>
          </p>
        </form>
      )}
    </main>
  );
}
